import { indexStartingBefore, type StripLayout } from './strip'

// Every statement gets this much of the bar before its pages count: the gap to its neighbour
// and enough body to be seen and hit on its own.
export const SLIVER_BASE = 4
// The gap between two statements, taken off the right of each sliver.
export const SEAM = 2
// Each page adds at least this much, so three pages and ten never look alike. A floor on the
// whole sliver instead made every small statement the same width.
export const PAGE_FLOOR = 2
export const SMALLEST_THUMB = 24

export interface Sliver {
  statementId: string
  left: number
  width: number
}

// The strip scaled down onto a bar that can be longer than the track. Every sliver is its base
// plus its pages at one scale, so strip offsets map onto the bar in straight pieces:
// `stripStops[i]` lands on `barStops[i]`, and anything between two stops is interpolated.
export interface Bar {
  slivers: Sliver[]
  width: number
  // Bar pixels per strip pixel, inside a sliver past its base and in the gaps between them.
  scale: number
  stripStops: number[]
  barStops: number[]
}

export interface ThumbPlacement {
  // How far the bar is slid left so the thumb stays on the track.
  offset: number
  thumbLeft: number
  thumbWidth: number
}

const sliverWidth = (stripWidth: number, scale: number) =>
  SLIVER_BASE + stripWidth * scale

// The scale that fits the bar to the track once every sliver has its base, unless that gives a
// page less than its floor. The floor wins, and the bar grows past the track by exactly what
// the pages need.
const scaleFor = (
  layout: StripLayout,
  scrollWidth: number,
  trackWidth: number,
) => {
  const bases = layout.bands.length * SLIVER_BASE
  const proportional = (trackWidth - bases) / scrollWidth
  const floor = PAGE_FLOOR / layout.metrics.pageStride
  return Math.max(proportional, floor)
}

export const EMPTY_BAR: Bar = {
  slivers: [],
  width: 0,
  scale: 0,
  stripStops: [0],
  barStops: [0],
}

export const barLayout = (
  layout: StripLayout,
  scrollWidth: number,
  trackWidth: number,
): Bar => {
  if (layout.bands.length === 0 || scrollWidth <= 0 || trackWidth <= 0) {
    return EMPTY_BAR
  }
  const scale = scaleFor(layout, scrollWidth, trackWidth)
  const stripStops = [0]
  const barStops = [0]
  const slivers: Sliver[] = []
  let stripAt = 0
  let barAt = 0

  const advance = (toStrip: number, byBar: number) => {
    stripAt = toStrip
    barAt += byBar
    stripStops.push(stripAt)
    barStops.push(barAt)
  }

  for (const band of layout.bands) {
    advance(band.left, (band.left - stripAt) * scale)
    const width = sliverWidth(band.width, scale)
    slivers.push({ statementId: band.statementId, left: barAt, width })
    advance(band.left + band.width, width)
  }
  advance(scrollWidth, (scrollWidth - stripAt) * scale)

  return { slivers, width: barAt, scale, stripStops, barStops }
}

// Where a strip offset lands on the bar.
export const toBar = (bar: Bar, stripX: number) => {
  const { stripStops, barStops } = bar
  const last = stripStops.length - 1
  if (stripX <= 0) return 0
  if (stripX >= stripStops[last]) return barStops[last]
  let low = 0
  let high = last - 1
  while (low < high) {
    const middle = Math.ceil((low + high) / 2)
    if (stripStops[middle] <= stripX) low = middle
    else high = middle - 1
  }
  const span = stripStops[low + 1] - stripStops[low]
  if (span === 0) return barStops[low]
  const along = (stripX - stripStops[low]) / span
  return barStops[low] + along * (barStops[low + 1] - barStops[low])
}

// Which sliver sits under a point on the bar, as an index into the layout's bands.
export const sliverIndexAt = (bar: Bar, barX: number) =>
  indexStartingBefore(bar.slivers, barX)

// The thumb's left edge sits on the bar exactly where the strip is scrolled to, and it walks the
// track from flush left to flush right as that point walks the bar. Whatever is left over is how
// far the bar slides the other way. The thumb keeps one width, the viewport at the bar's
// proportional scale, so it does not breathe as it crosses statements held at the floor.
export const placeThumb = (
  bar: Bar,
  trackWidth: number,
  scrollLeft: number,
  viewportWidth: number,
  scrollWidth: number,
): ThumbPlacement => {
  const start = toBar(bar, scrollLeft)
  const farthest = toBar(bar, Math.max(scrollWidth - viewportWidth, 0))
  const thumbWidth = Math.max(viewportWidth * bar.scale, SMALLEST_THUMB)
  const travel = Math.max(trackWidth - thumbWidth, 0)
  const thumbLeft = farthest === 0 ? 0 : (start / farthest) * travel
  return { offset: start - thumbLeft, thumbLeft, thumbWidth }
}

const SCROLL_SEARCH_STEPS = 40

// The scroll that puts the thumb's left edge here. The thumb climbs the track as the strip
// scrolls, so the answer is found by halving. This is what lets a dragged thumb stay under the
// pointer while the bar slides at its own rate underneath.
export const scrollForThumb = (
  bar: Bar,
  trackWidth: number,
  thumbLeft: number,
  viewportWidth: number,
  scrollWidth: number,
) => {
  let low = 0
  let high = Math.max(scrollWidth - viewportWidth, 0)
  for (let step = 0; step < SCROLL_SEARCH_STEPS; step += 1) {
    const middle = (low + high) / 2
    const placed = placeThumb(
      bar,
      trackWidth,
      middle,
      viewportWidth,
      scrollWidth,
    )
    if (placed.thumbLeft < thumbLeft) low = middle
    else high = middle
  }
  return low
}
